import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { PushNotifications } from '@capacitor/push-notifications';
import type { PlanletsEnv } from '../auth/config';
import { createNativePlatform, type AuthSessionLike, type SecureStoreLike } from './native';
import type { Platform } from './platform';
import { createWebPlatform } from './web';

/** The in-repo secure-storage plugin the shells implement (ADR-029 §4). */
export const SecureStore = registerPlugin<SecureStoreLike>('SecureStore');

/** The in-repo iOS authentication-session plugin (ADR-029 §4); unimplemented elsewhere. */
export const AuthSession = registerPlugin<AuthSessionLike>('AuthSession');

/** The environment a shell was built for (`VITE_PLANLETS_ENV`); anything but `prod` is dev. */
export function envFromBuild(value: string | undefined): PlanletsEnv {
  return value === 'prod' ? 'prod' : 'dev';
}

/** The environment this build targets — meaningful inside a shell (the page reads its hostname). */
export const buildEnv: PlanletsEnv = envFromBuild(import.meta.env.VITE_PLANLETS_ENV as string | undefined);

/** Picks the platform the running page is: a native shell, or the browser. */
export function selectPlatform(): Platform {
  if (!Capacitor.isNativePlatform()) return createWebPlatform();
  return createNativePlatform({
    kind: Capacitor.getPlatform() === 'ios' ? 'ios' : 'android',
    env: envFromBuild(import.meta.env.VITE_PLANLETS_ENV as string | undefined),
    app: App,
    browser: Browser,
    preferences: Preferences,
    secureStore: SecureStore,
    authSession: AuthSession,
    push: PushNotifications,
    capabilities: {
      secureContext: window.isSecureContext,
      subtleCrypto: typeof crypto.subtle !== 'undefined',
      randomUUID: typeof crypto.randomUUID === 'function',
    },
  });
}

/** The one platform of this page — what `main.tsx` and `App.tsx` bind to. */
export const platform: Platform = selectPlatform();
